import L from 'leaflet';
import React from 'react';
import { extractPersonnelIsSupervisor } from 'features/Responder/features/Personnel/features/PersonnelList/utils/personnelListUtils';
import {
  getPersonnelIconColor,
  getPersonnelInitials,
} from 'features/Responder/features/Schedules/features/Assignments/components/AssignmentBuilder/components/AssignmentAssigner/components/PersonnelIcon';
import { MdSecurity } from 'react-icons/md';
import { renderToStaticMarkup } from 'react-dom/server';

const PIN_SVG = (color = '#2e7be7') => `
<svg width="25" height="41" viewBox="0 0 25 41" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12.5 0C5.59644 0 0 5.59236 0 12.4918C0 22.0111 12.5 41 12.5 41C12.5 41 25 22.0111 25 12.4918C25 5.59236 19.4036 0 12.5 0Z" fill="${color}" stroke="white" stroke-width="2"/>
  <circle cx="12.5" cy="12.5" r="4.5" fill="white" opacity="0.85"/>
</svg>
`;

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const PERSONNEL_ICON_CACHE = new Map();
const PERSONNEL_SUPERVISOR_GLYPH = renderToStaticMarkup(
  React.createElement(MdSecurity, { size: 10, color: '#ffffff' }),
);

export const buildPinIcon = (color = '#2e7be7') =>
  L.divIcon({
    className: 'checkpoint-pin-icon',
    html: PIN_SVG(color),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

export const buildLabeledPinIcon = (color = '#2e7be7', label = '') => {
  const safeLabel = escapeHtml(label).trim();
  if (!safeLabel) return buildPinIcon(color);

  return L.divIcon({
    className: 'responder-personnel-pin-wrapper',
    html: `
      <div class="responder-personnel-pin-icon" title="${safeLabel}">
        <div class="responder-personnel-pin-label">${safeLabel}</div>
        <div class="responder-personnel-pin-svg">${PIN_SVG(color)}</div>
      </div>
    `,
    iconSize: [140, 56],
    iconAnchor: [70, 56],
    popupAnchor: [0, -56],
    tooltipAnchor: [0, -56],
  });
};

export const buildPersonnelMarkerIcon = ({ person, label = '', showLabel = false } = {}) => {
  const displayLabel = escapeHtml(label).trim();
  const isOffline = !Boolean(person?.clockedIn);
  const isSupervisor = extractPersonnelIsSupervisor(person);
  const baseColor = getPersonnelIconColor(person);
  const initials = escapeHtml(getPersonnelInitials(person));

  const cacheKey = [
    displayLabel,
    isOffline ? 'offline' : 'online',
    isSupervisor ? 'supervisor' : 'standard',
    baseColor,
    initials,
    showLabel ? 'labeled' : 'compact',
  ].join('|');

  if (PERSONNEL_ICON_CACHE.has(cacheKey)) {
    return PERSONNEL_ICON_CACHE.get(cacheKey);
  }

  const avatar = `
    <div class="responder-personnel-avatar${isOffline ? ' is-offline' : ''}${isSupervisor ? ' is-supervisor' : ''}" style="--personnel-avatar-color:${baseColor}">
      <span class="responder-personnel-avatar-initials">${initials}</span>
      ${
        isSupervisor
          ? `<span class="responder-personnel-avatar-supervisor">${PERSONNEL_SUPERVISOR_GLYPH}</span>`
          : ''
      }
    </div>
  `;

  const html = showLabel
    ? `
      <div class="responder-personnel-avatar-icon responder-personnel-avatar-icon--labeled">
        <div class="responder-personnel-pin-label">${displayLabel}</div>
        ${avatar}
      </div>
    `
    : `
      <div class="responder-personnel-avatar-icon responder-personnel-avatar-icon--compact">
        ${avatar}
      </div>
    `;

  const icon = L.divIcon({
    className: 'responder-personnel-avatar-marker',
    html,
    iconSize: showLabel ? [140, 68] : [48, 48],
    iconAnchor: showLabel ? [70, 58] : [24, 42],
    popupAnchor: showLabel ? [0, -58] : [0, -38],
    tooltipAnchor: showLabel ? [0, -58] : [0, -34],
  });

  PERSONNEL_ICON_CACHE.set(cacheKey, icon);
  return icon;
};
